<template>
  <div class="bbm-dir">

    <!-- Sub-nav expertise pillars -->
    <div class="bbm-subnav">
      <div class="bbm-subnav__inner">
        <span
          v-for="pillar in visiblePillars"
          :key="pillar.key"
          :class="['bbm-subnav__link', activePillar === pillar.key ? 'active' : '']"
          @click="activePillar = pillar.key"
        >{{ pillar.label }}</span>
      </div>
    </div>

    <main class="bbm-dir__main">

      <!-- Hero Banner -->
      <section class="bbm-hero">
        <div class="bbm-hero__content">
          <h1 class="bbm-hero__title">{{ currentPillar.heroTitle }}</h1>
          <p class="bbm-hero__sub">{{ currentPillar.heroSub }}</p>
        </div>
        <div class="bbm-hero__overlay"></div>
      </section>

      <!-- Premium Partners -->
      <section v-if="pillarActorsLoading || pillarPartners.length" class="bbm-section">
        <h2 class="bbm-section__title tw-flex items-center gap-2" style="margin-bottom: 24px;">
          <span class="material-symbols-outlined bbm-section__icon">star</span>
          Premium Partners
        </h2>
        <!-- Loading skeleton -->
        <div v-if="pillarActorsLoading" class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div v-for="n in 2" :key="n" class="bbm-skeleton-card">
            <div class="bbm-skeleton bbm-skeleton--image bbm-skeleton--image-lg"></div>
            <div style="padding: 24px;">
              <div class="bbm-skeleton bbm-skeleton--title"></div>
              <div class="bbm-skeleton bbm-skeleton--text"></div>
              <div class="bbm-skeleton bbm-skeleton--text" style="width: 60%"></div>
              <div class="bbm-skeleton bbm-skeleton--btn" style="margin-top: 24px;"></div>
            </div>
          </div>
        </div>
        <!-- Carousel -->
        <template v-else-if="pillarPartners.length">
          <div ref="partnersCarousel" class="bbm-carousel" :style="{ '--cards': cardsPerView }">
            <div
              v-for="partner in pillarPartners"
              :key="partner.id"
              class="bbm-featured-card bbm-carousel__item group"
              @click="goToPartner(partner)"
            >
              <div class="bbm-featured-card__image">
                <img :src="actorImage(partner)" :alt="partner.name"/>
                <span class="bbm-badge bbm-badge--member">
                  <span class="material-symbols-outlined" style="font-size:12px;">verified</span>
                  PARTNER
                </span>
              </div>
              <div class="bbm-featured-card__body">
                <div class="tw-flex justify-between items-start" style="margin-bottom: 8px;">
                  <h3 class="bbm-featured-card__title">{{ partner.name }}</h3>
                  <!--                  <span v-if="partner.actor_type" class="bbm-tag">{{ partner.actor_type }}</span>-->
                </div>
                <p class="bbm-featured-card__desc">{{ actorDescription(partner) }}</p>
                <button class="bbm-btn-accent">View Full Profile</button>
              </div>
            </div>
          </div>
          <div v-if="pillarPartners.length > 2" class="bbm-carousel-dots">
            <span
              v-for="(_, i) in pillarPartners"
              :key="i"
              class="bbm-carousel-dot"
              :class="{ 'bbm-carousel-dot--active': partnersCarouselIndex === i }"
              @click="goToCard('partnersCarousel', 'partnersCarouselIndex', i)"
            ></span>
          </div>
        </template>
        <!-- Empty state -->
        <div v-else class="bbm-empty">
          <span class="material-symbols-outlined bbm-empty__icon">star</span>
          <p>No premium partners available yet.</p>
        </div>
      </section>

      <!-- Premium Hubs -->
      <section v-if="pillarActorsLoading || pillarHubs.length" class="bbm-section">
        <div class="tw-flex justify-between items-center" style="margin-bottom: 32px;">
          <h2 class="bbm-section__title tw-flex items-center gap-2">
            <span class="material-symbols-outlined bbm-section__icon">apartment</span>
            Premium Hubs
          </h2>
          <span class="bbm-link cursor-pointer" @click="$router.push('/hubs-overview-brussels')">
            View all hubs <span class="material-symbols-outlined" style="font-size:14px; vertical-align: middle;">arrow_forward</span>
          </span>
        </div>
        <!-- Loading skeleton -->
        <div v-if="pillarActorsLoading" class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div v-for="n in 2" :key="n" class="bbm-skeleton-card">
            <div class="bbm-skeleton bbm-skeleton--image bbm-skeleton--image-lg"></div>
            <div style="padding: 24px;">
              <div class="bbm-skeleton bbm-skeleton--title"></div>
              <div class="bbm-skeleton bbm-skeleton--text"></div>
              <div class="bbm-skeleton bbm-skeleton--text" style="width: 60%"></div>
              <div class="bbm-skeleton bbm-skeleton--btn" style="margin-top: 24px;"></div>
            </div>
          </div>
        </div>
        <!-- Carousel -->
        <template v-else-if="pillarHubs.length">
          <div ref="hubsCarousel" class="bbm-carousel" :style="{ '--cards': cardsPerView }">
            <div
              v-for="hub in pillarHubs"
              :key="hub.id"
              class="bbm-featured-card bbm-carousel__item group"
              @click="goToHub(hub)"
            >
              <div class="bbm-featured-card__image">
                <img :src="actorImage(hub)" :alt="hub.name"/>
                <span class="bbm-badge bbm-badge--member">
                  <span class="material-symbols-outlined" style="font-size:12px;">apartment</span>
                  HUB
                </span>
              </div>
              <div class="bbm-featured-card__body">
                <div class="tw-flex justify-between items-start" style="margin-bottom: 8px;">
                  <h3 class="bbm-featured-card__title">{{ hub.name }}</h3>
                  <!--                  <span v-if="hub.actor_type" class="bbm-tag">{{ hub.actor_type }}</span>-->
                </div>
                <p class="bbm-featured-card__desc">{{ actorDescription(hub) }}</p>
                <button class="bbm-btn-accent">View Hub</button>
              </div>
            </div>
          </div>
          <div v-if="pillarHubs.length > 2" class="bbm-carousel-dots">
            <span
              v-for="(_, i) in pillarHubs"
              :key="i"
              class="bbm-carousel-dot"
              :class="{ 'bbm-carousel-dot--active': hubsCarouselIndex === i }"
              @click="goToCard('hubsCarousel', 'hubsCarouselIndex', i)"
            ></span>
          </div>
        </template>
        <!-- Empty state -->
        <div v-else class="bbm-empty">
          <span class="material-symbols-outlined bbm-empty__icon">apartment</span>
          <p>No premium hubs available yet.</p>
        </div>
      </section>

      <!-- Service Directory -->
      <section class="bbm-section">
        <div class="tw-flex justify-between items-center" style="margin-bottom: 24px;">
          <h2 class="bbm-section__title">Ecosystem Members</h2>
          <div class="bbm-search">
            <span class="material-symbols-outlined bbm-search__icon">search</span>
            <input
              v-model="searchQuery"
              class="bbm-search__input"
              placeholder="Search providers..."
              type="text"
            />
          </div>
        </div>

        <!-- Loading skeleton -->
        <div v-if="pillarActorsLoading" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div v-for="n in 6" :key="n" class="bbm-skeleton-card" style="padding: 24px;">
            <div class="tw-flex items-center gap-4" style="margin-bottom: 12px;">
              <div class="bbm-skeleton" style="width:48px;height:48px;border-radius:9999px;flex-shrink:0;"></div>
              <div class="bbm-skeleton bbm-skeleton--title" style="margin:0;flex:1;"></div>
            </div>
            <div class="bbm-skeleton bbm-skeleton--text"></div>
            <div class="bbm-skeleton bbm-skeleton--text" style="width: 70%"></div>
          </div>
        </div>

        <template v-else-if="membersPages.length">
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div
              v-for="provider in membersPages[membersPage]"
              :key="provider.id || provider.name"
              class="bbm-provider-card"
              @click="goToLogin(provider)"
            >
              <!-- Status bar — top of card, above everything -->
              <div class="bbm-provider-card__status-bar" style="text-align: right;">
                <span v-if="provider.claimed" class="bbm-verified-badge">
                  <span class="material-symbols-outlined" style="font-size:13px;font-variation-settings:'FILL' 1,'wght' 400,'GRAD' 0,'opsz' 24;">check_circle</span>
                  Verified
                </span>
                <button v-else class="bbm-claim-btn" @click.stop="goToLogin(provider)">
                  <span class="material-symbols-outlined" style="font-size:13px;">add_circle</span>
                  Verify
                </button>
              </div>
              <!-- Logo + name -->
              <div class="tw-flex items-center gap-4" style="margin-bottom: 12px;">
                <div class="bbm-provider-card__icon-wrap">
                  <img :src="actorImage(provider)" :alt="provider.name" class="bbm-provider-card__img"/>
                </div>
                <div style="flex: 1; min-width: 0;">
                  <h4 class="bbm-provider-card__name">{{ provider.name }}</h4>
                </div>
              </div>
              <p class="bbm-provider-card__desc">{{ actorDescription(provider) }}</p>
            </div>
          </div>

          <div v-if="membersPages.length > 1" class="bbm-carousel-dots">
            <span
              v-for="(_, i) in membersPages"
              :key="i"
              class="bbm-carousel-dot"
              :class="{ 'bbm-carousel-dot--active': membersPage === i }"
              @click="membersPage = i"
            ></span>
          </div>
        </template>

        <!-- Empty state -->
        <div v-else class="bbm-empty">
          <span class="material-symbols-outlined bbm-empty__icon">{{ searchQuery ? 'search_off' : 'group' }}</span>
          <p>{{ searchQuery ? `No providers found for "${searchQuery}"` : 'No members available yet.' }}</p>
        </div>
      </section>

      <!-- Expert Help CTA -->
      <section class="bbm-section">
        <div class="bbm-help-cta">
          <div class="tw-flex items-center gap-6">
            <div class="bbm-help-cta__icon-wrap">
              <span class="material-symbols-outlined bbm-help-cta__icon">support_agent</span>
            </div>
            <div>
              <h4 class="bbm-help-cta__title">Need guidance?</h4>
              <p class="bbm-help-cta__body">Our ecosystem managers can help match you with the right service provider.</p>
            </div>
          </div>
          <button class="bbm-btn-primary tw-flex items-center gap-2" @click="onContactSupport()">
            Contact Support
            <span class="material-symbols-outlined" style="font-size:16px;">open_in_new</span>
          </button>
        </div>
      </section>

    </main>

    <!-- Map Section -->
    <section class="bbm-map-section">
      <div class="bbm-container" style="margin-bottom: 32px;">
        <h2 class="bbm-section__title">Ecosystem Geography</h2>
        <p class="bbm-map-section__sub">Explore the density of business support services across the Brussels Capital Region.</p>
      </div>
      <div class="bbm-map-frame-wrap">
        <iframe
          allow="geolocation"
          class="bbm-map-frame"
          src="https://brussels.datascouts.eu/ecosystem-maps/8/embed?apiKey=ds_live_VFyoesIWR0eSNrSez2aE"
          title="Brussels Ecosystem Map"
        ></iframe>
      </div>
    </section>

    <SimplifiedFooterBrussels/>

  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import { trackHeapEvent } from '@/util/analytics'
  import { fetchActors } from '@/api/actors'
  import { getDefaultFallbackImageUrl } from '@/util/helpers'
  import SimplifiedFooterBrussels from '@/components/SimplifiedFooter/SimplifiedFooterBrussels.vue'
  import { trackBECICta, trackBECITabSwitch, trackBECICard, createScrollDepthTracker } from '@/util/useBECITracking.js'

  const PILLARS = [
    {
      key: 'business-support',
      portfolioHubs: 'EGnR8qWxa7',
      portfolioPartners: 'lDnrgOQkQJ',
      portfolioMembers: '9exXW28kbl',
      label: 'Business Support',
      heroTitle: 'Business Support in Brussels',
      heroSub: 'Who can support my business journey in Brussels?',
    },
    {
      key: 'consulting',
      portfolioHubs: 'ZgkvQE6nro',
      portfolioPartners: 'bGnGazvxNX',
      portfolioMembers: 'oDVKOmWV6r',
      label: 'Consulting & Expertise',
      heroTitle: 'Consulting & Expertise in Brussels',
      heroSub: 'Who can support my project with specific expertise?',
    },
    {
      key: 'entrepreneurship',
      portfolioHubs: '9jVOMldkOv',
      portfolioPartners: 'RLn7E5Qx0w',
      portfolioMembers: 'WKkNMJDnzw',
      label: 'Entrepreneurship & Innovation',
      heroTitle: 'Entrepreneurship & Innovation in Brussels',
      heroSub: 'Where can I innovate, grow and launch a business in Brussels?',
    },
    {
      key: 'talent',
      portfolioHubs: 'AekDJgPV24',
      portfolioPartners: 'eykzYL1nB4',
      portfolioMembers: '4Nn37aJn0D',
      label: 'Talent & Human Capital',
      heroTitle: 'Talent & Human Capital in Brussels',
      heroSub: 'Who can help me recruit, manage or train my team in Brussels?',
    },
    {
      key: 'visibility',
      portfolioHubs: 'EJnJPRQxKq',
      portfolioPartners: 'JjVgN35x2p',
      portfolioMembers: 'YJnwY0Yxlp',
      label: 'Network & Representation',
      heroTitle: 'Network & Representation in Brussels',
      heroSub: 'How can I expand my network or represent my company in Brussels?',
    },
    {
      key: 'workspace',
      portfolioHubs: 'Mlkyedqky8',
      portfolioPartners: 'GZx6lo0n8A',
      portfolioMembers: 'e2VQp09xaD',
      label: 'Working space in Brussels',
      heroTitle: 'Working Space in Brussels',
      heroSub: 'Where can I work or host an event in Brussels?',
    },
  ]


  export default defineComponent({
    name: 'StaticHomeBrusselsDirectory',
    components: { SimplifiedFooterBrussels },
    data() {
      return {
        activePillar: 'business-support' as string,
        searchQuery: '' as string,
        pillars: PILLARS,
        pillarActors: [] as any[],
        pillarActorsLoading: false,
        pillarHubs: [] as any[],
        pillarPartners: [] as any[],
        pillarCounts: {} as Record<string, number>,
        partnersCarouselIndex: 0,
        hubsCarouselIndex: 0,
        membersPage: 0,
        _partnersTimer: null as any,
        _hubsTimer: null as any,
        cardsPerView: window.innerWidth < 768 ? 1 : 2,
      }
    },
    computed: {
      currentPillar() {
        return this.pillars.find((p: any) => p.key === this.activePillar) || this.pillars[0]
      },
      visiblePillars() {
        return this.pillars.filter((p: any) => {
          const count = this.pillarCounts[p.key]
          // show if not yet checked (undefined) or has actors
          return count === undefined || count > 0
        })
      },
      filteredProviders() {
        if (!this.searchQuery.trim()) return this.pillarActors
        const q = this.searchQuery.toLowerCase()
        return this.pillarActors.filter((p: any) =>
          (p.name || '').toLowerCase().includes(q) ||
          (p.short_description || p.activities_description || '').toLowerCase().includes(q)
        )
      },
      membersPages() {
        const sorted = [...this.filteredProviders].sort((a: any, b: any) =>
          (b.claimed ? 1 : 0) - (a.claimed ? 1 : 0)
        )
        const pages: any[][] = []
        for (let i = 0; i < sorted.length; i += 6) {
          pages.push(sorted.slice(i, i + 6))
        }
        return pages
      },
    },
    async mounted() {
      this._scrollTracker = createScrollDepthTracker('BECI Directory')
      this._scrollTracker.start()
      const pillarParam = this.$route.query.pillar as string
      if (pillarParam && this.pillars.some((p: any) => p.key === pillarParam)) {
        this.activePillar = pillarParam
      }
      this.loadAllPillarCounts()
      await this.loadPillarActors()
      this.startCarousels()
      window.addEventListener('resize', this._onResize)
    },
    beforeUnmount() {
      this._scrollTracker?.stop()
      this.stopCarousels()
      window.removeEventListener('resize', this._onResize)
    },
    watch: {
      activePillar(newVal: string) {
        const pillar = this.pillars.find((p: any) => p.key === newVal)
        if (pillar) trackBECITabSwitch('Directory', pillar.label)
        this.partnersCarouselIndex = 0
        this.hubsCarouselIndex = 0
        this.membersPage = 0
        this.stopCarousels()
        this.loadPillarActors().then(() => this.startCarousels())
      },
      searchQuery() {
        this.membersPage = 0
      },
    },
    methods: {
      goToLogin(actor: any) {
        trackBECICard('Directory Member', actor.name)
        if (this.$store.getters.isLoggedIn) {
          this.$router.push(`/actors/${actor.id}`)
        } else {
          this.$router.push({ path: '/login', query: { external: 'view-actor', actor_id: actor.id, actor_name: actor.name } })
        }
      },
      goToHub(hub: any) {
        trackBECICard('Directory Hub', hub.name)
        if (this.$store.getters.isLoggedIn) {
          this.$router.push(`/actors/${hub.id}`)
        } else {
          this.$router.push(`/hubs-brussels/${hub.id}`)
        }
      },
      goToPartner(partner: any) {
        trackBECICard('Directory Partner', partner.name)
        if (this.$store.getters.isLoggedIn) {
          this.$router.push(`/actors/${partner.id}`)
        } else {
          this.$router.push(`/partners-brussels/${partner.id}`)
        }
      },
      onContactSupport() {
        trackBECICta('Directory', 'Contact Support', 'Help CTA')
        this.$router.push('/login')
      },
      async loadAllPillarCounts() {
        const counts: Record<string, number> = {}
        await Promise.all(
          this.pillars.map(async (pillar: any) => {
            const ids = [pillar.portfolioHubs, pillar.portfolioPartners, pillar.portfolioMembers].filter(Boolean)
            if (!ids.length) {
              counts[pillar.key] = 0
              return
            }
            const results = await Promise.all(
              ids.map((id: string) =>
                fetchActors({ portfolio: id, limit: 1 }).then((r: any) => (r.data || []).length).catch(() => 0)
              )
            )
            counts[pillar.key] = results.reduce((a: number, b: number) => a + b, 0)
          })
        )
        this.pillarCounts = counts
        // If active pillar got hidden, switch to first visible one
        if (counts[this.activePillar] === 0) {
          const first = this.pillars.find((p: any) => counts[p.key] > 0)
          if (first) this.activePillar = first.key
        }
      },
      async loadPillarActors() {
        const pillar: any = this.pillars.find((p: any) => p.key === this.activePillar)
        if (!pillar) return
        this.pillarActorsLoading = true
        this.pillarHubs = []
        this.pillarPartners = []
        this.pillarActors = []
        const safe = (promise: Promise<any>) => promise.catch(() => ({ data: [] }))
        const [hubsResult, partnersResult, membersResult] = await Promise.all([
          pillar.portfolioHubs ? safe(fetchActors({ portfolio: pillar.portfolioHubs, limit: 3 })) : Promise.resolve({ data: [] }),
          pillar.portfolioPartners ? safe(fetchActors({ portfolio: pillar.portfolioPartners, limit: 3 })) : Promise.resolve({ data: [] }),
          pillar.portfolioMembers ? safe(fetchActors({ portfolio: pillar.portfolioMembers, limit: 60 })) : Promise.resolve({ data: [] }),
        ])
        this.pillarHubs = (hubsResult as any).data || []
        this.pillarPartners = (partnersResult as any).data || []
        this.pillarActors = (membersResult as any).data || []
        this.pillarActorsLoading = false
      },
      actorImage(actor: any) {
        return actor.featured_image_url || actor.website_screenshot || getDefaultFallbackImageUrl()
      },
      actorDescription(actor: any): string {
        return actor.short_description || actor.activities_description || ''
      },
      trackHeapEvent,
      startCarousels() {
        this._partnersTimer = setInterval(() => this.advanceCarousel('partnersCarousel', 'partnersCarouselIndex', 'pillarPartners'), 4000)
        this._hubsTimer = setInterval(() => this.advanceCarousel('hubsCarousel', 'hubsCarouselIndex', 'pillarHubs'), 4500)
      },
      stopCarousels() {
        clearInterval(this._partnersTimer)
        clearInterval(this._hubsTimer)
      },
      advanceCarousel(refName: string, indexProp: string, listProp: string) {
        const items: any[] = (this as any)[listProp]
        if (items.length <= 2) return
        const next = ((this as any)[indexProp] + 1) % items.length
        this.goToCard(refName, indexProp, next)
      },
      _onResize() {
        this.cardsPerView = window.innerWidth < 768 ? 1 : 2
      },
      goToCard(refName: string, indexProp: string, index: number) {
        (this as any)[indexProp] = index
        const el = (this.$refs as any)[refName] as HTMLElement
        if (!el) return
        const gap = 24
        const itemWidth = (el.offsetWidth - gap * (this.cardsPerView - 1)) / this.cardsPerView
        el.scrollTo({ left: index * (itemWidth + gap), behavior: 'smooth' })
      },
    },
  })
</script>

<style scoped lang="scss">
  @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Inter:wght@400;500;600;700&display=swap');
  @import url('https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:wght,FILL@100..700,0..1&display=swap');

  $bbm-primary: var(--primary-community);
  $bbm-primary-container: var(--primary-community);
  $bbm-accent: #ff6f31;
  $bbm-accent-dark: #802a00;
  $bbm-secondary: #00677f;
  $bbm-secondary-container: #00ccf9;
  $bbm-surface: #f7f9fb;
  $bbm-muted: #43474f;

  .material-symbols-outlined {
    font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24;
  }

  .bbm-dir {
    font-family: 'Inter', sans-serif;
    background: $bbm-surface;
    color: #191c1e;
    overflow-x: hidden;
  }

  // Sub-nav
  .bbm-subnav {
    position: sticky;
    z-index: 40;
    background: white;
    border-bottom: 1px solid #f1f5f9;
    overflow-x: auto;
    white-space: nowrap;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .bbm-subnav__inner {
    max-width: 1280px;
    margin: 0 auto;
    padding: 0 32px;
    display: flex;
    gap: 32px;
    height: 48px;
    align-items: center;
  }

  .bbm-subnav__link {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 0.875rem;
    font-weight: 500;
    color: #64748b;
    height: 100%;
    display: inline-flex;
    align-items: center;
    border-bottom: 2px solid transparent;
    cursor: pointer;
    transition: color 0.15s, border-color 0.15s;
    white-space: nowrap;

    &:hover {
      color: $bbm-primary-container;
    }

    &.active {
      color: $bbm-primary-container;
      font-weight: 700;
      border-bottom-color: $bbm-primary-container;
    }
  }

  // Main content
  .bbm-dir__main {
    max-width: 1280px;
    margin: 0 auto;
    padding: 0 32px 80px;
  }

  // Hero
  .bbm-hero {
    position: relative;
    border-radius: 12px;
    overflow: hidden;
    background: $bbm-primary-container;
    padding: 48px;
    margin: 48px 0;
    color: white;

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(to right, rgba(0, 30, 64, 0.6), transparent);
      pointer-events: none;
    }
  }

  .bbm-hero__content {
    position: relative;
    z-index: 1;
    max-width: 600px;
  }

  .bbm-hero__title {
    font-family: 'Space Grotesk', sans-serif;
    font-size: clamp(1.75rem, 3vw, 3rem);
    font-weight: 700;
    letter-spacing: -0.02em;
    line-height: 1.1;
    color: white;
    margin: 0 0 16px;
  }

  .bbm-hero__sub {
    font-size: 1.125rem;
    line-height: 1.6;
    color: #cbd5e1;
    margin: 0;
  }

  // Sections
  .bbm-section {
    margin-bottom: 64px;
  }

  .bbm-container {
    max-width: 1280px;
    margin: 0 auto;
    padding: 0 32px;
  }

  .bbm-section__title {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 1.75rem;
    font-weight: 600;
    color: $bbm-primary;
    margin: 0;
    line-height: 1.2;
  }

  .bbm-section__icon {
    color: $bbm-secondary-container;
    font-size: 1.5rem;
  }

  // Links
  .bbm-link {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 0.875rem;
    font-weight: 700;
    color: $bbm-primary-container;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    transition: gap 0.15s, text-decoration 0.15s;

    &:hover {
      text-decoration: underline;
    }

    &--sm {
      font-size: 0.8125rem;
      color: $bbm-secondary;
    }
  }

  // Carousel
  .bbm-carousel {
    display: flex;
    gap: 24px;
    overflow-x: hidden;
    scroll-snap-type: x mandatory;
  }

  .bbm-carousel__item {
    flex: 0 0 calc((100% - 24px * (var(--cards, 2) - 1)) / var(--cards, 2));
    min-width: 0;
    scroll-snap-align: start;
  }

  .bbm-carousel-dots {
    display: flex;
    justify-content: center;
    gap: 8px;
    margin-top: 20px;
  }

  .bbm-carousel-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #cbd5e1;
    cursor: pointer;
    transition: background 0.2s, transform 0.2s;

    &--active {
      background: $bbm-primary-container;
      transform: scale(1.3);
    }

    &:hover {
      background: $bbm-primary-container;
      opacity: 0.7;
    }
  }

  // Hub cards
  .bbm-hub-card {
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    cursor: pointer;
    transition: box-shadow 0.25s;

    &:hover {
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
    }
  }

  .bbm-hub-card__image {
    height: 192px;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.5s;

      .bbm-hub-card:hover & {
        transform: scale(1.05);
      }
    }
  }

  .bbm-hub-card__body {
    padding: 20px;
  }

  .bbm-hub-card__title {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 1.1rem;
    font-weight: 600;
    color: $bbm-primary;
    margin: 0 0 8px;
  }

  .bbm-hub-card__desc {
    font-size: 0.875rem;
    line-height: 1.5;
    color: #475569;
    margin: 0 0 24px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .bbm-hub-card__btn {
    width: 100%;
    padding: 8px 0;
    background: transparent;
    color: $bbm-primary-container;
    border: 2px solid $bbm-primary-container;
    border-radius: 8px;
    font-weight: 700;
    font-size: 0.875rem;
    cursor: pointer;
    transition: background 0.2s, color 0.2s;

    &:hover {
      background: $bbm-primary-container;
      color: white;
    }
  }

  // Featured partner cards
  .bbm-featured-card {
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    cursor: pointer;
    transition: box-shadow 0.25s;

    &:hover {
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
    }
  }

  .bbm-featured-card__image {
    height: 256px;
    position: relative;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.5s;

      .bbm-featured-card:hover & {
        transform: scale(1.04);
      }
    }
  }

  .bbm-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 0.625rem;
    font-weight: 700;
    letter-spacing: 0.04em;

    &--member {
      position: absolute;
      top: 16px;
      left: 16px;
      background: $bbm-primary-container;
      color: white;
      padding: 4px 8px;
      border-radius: 2px;
    }

    &--member-sm {
      background: rgba(0, 51, 102, 0.06);
      color: $bbm-primary-container;
      padding: 3px 8px;
      border-radius: 9999px;
    }

  }

  .bbm-featured-card__body {
    padding: 24px;
  }

  .bbm-featured-card__title {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 1.25rem;
    font-weight: 600;
    color: $bbm-primary;
    margin: 0;
  }

  .bbm-tag {
    font-size: 0.75rem;
    font-weight: 700;
    color: $bbm-secondary;
    background: rgba(0, 204, 249, 0.1);
    padding: 4px 8px;
    border-radius: 4px;
    white-space: nowrap;
  }

  .bbm-featured-card__desc {
    font-size: 0.9375rem;
    line-height: 1.5;
    color: #475569;
    margin: 0 0 24px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  // Search
  .bbm-search {
    display: flex;
    align-items: center;
    gap: 8px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 6px 12px;
    background: white;
  }

  .bbm-search__icon {
    color: #94a3b8;
    font-size: 1rem;
  }

  .bbm-search__input {
    border: none;
    outline: none;
    font-size: 0.875rem;
    width: 192px;
    background: transparent;
    color: #191c1e;

    &::placeholder {
      color: #94a3b8;
    }
  }

  // Provider cards
  .bbm-provider-card {
    background: white;
    padding: 24px;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    cursor: pointer;
    transition: border-color 0.2s;

    &:hover {
      border-color: $bbm-secondary-container;
    }
  }

  .bbm-provider-card__icon-wrap {
    width: 48px;
    height: 48px;
    background: #f1f5f9;
    border-radius: 9999px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .bbm-provider-card__img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 9999px;
  }

  .bbm-provider-card__name {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 1rem;
    font-weight: 600;
    color: #191c1e;
    margin: 0;
  }

  .bbm-provider-card__cat {
    font-size: 0.75rem;
    color: #94a3b8;
    margin: 2px 0 0;
  }

  .bbm-provider-card__desc {
    font-size: 0.875rem;
    line-height: 1.5;
    color: #475569;
    margin: 0 0 16px;
  }

  // Provider card status bar
  .bbm-provider-card__status-bar {
    margin-bottom: 12px;
    min-height: 26px;
  }

  .bbm-verified-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 0.75rem;
    font-weight: 500;
    color: #1d4ed8;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    padding: 3px 9px;
    border-radius: 9999px;
  }

  .bbm-claim-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 0.75rem;
    font-weight: 700;
    font-family: inherit;
    color: #166534;
    background: #dcfce7;
    border: 1px solid #bbf7d0;
    padding: 3px 9px;
    border-radius: 9999px;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s;

    &:hover {
      background: #bbf7d0;
      border-color: #86efac;
    }
  }

  // Members login gate
  .bbm-members-gate {
    margin-top: 32px;
    border-radius: 16px;
    border: 1px solid #e2e8f0;
    background: linear-gradient(to bottom, rgba(247, 249, 251, 0) 0%, #f0f4f8 100%);
    padding: 48px 32px;
    text-align: center;
  }

  .bbm-members-gate__inner {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    max-width: 400px;
    margin: 0 auto;
  }

  .bbm-members-gate__icon-wrap {
    width: 64px;
    height: 64px;
    border-radius: 16px;
    background: #e2e8f0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #94a3b8;
    margin-bottom: 4px;
  }

  .bbm-members-gate__title {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 1.25rem;
    font-weight: 700;
    color: #191c1e;
    margin: 0;
  }

  .bbm-members-gate__body {
    font-size: 0.9375rem;
    color: #475569;
    margin: 0 0 8px;
  }

  .bbm-btn-gate-outline {
    padding: 12px 32px;
    background: white;
    color: $bbm-primary;
    font-family: 'Space Grotesk', sans-serif;
    font-weight: 700;
    font-size: 0.9375rem;
    border: 2px solid $bbm-primary;
    border-radius: 8px;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.15s, color 0.15s;

    &:hover {
      background: $bbm-primary;
      color: white;
    }
  }

  // Skeleton loading
  @keyframes shimmer {
    0% {
      background-position: -400px 0;
    }
    100% {
      background-position: 400px 0;
    }
  }

  .bbm-skeleton-card {
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    overflow: hidden;
  }

  .bbm-skeleton {
    background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
    background-size: 400px 100%;
    animation: shimmer 1.4s infinite;
    border-radius: 4px;
  }

  .bbm-skeleton--image {
    height: 192px;
    border-radius: 0;
    margin-bottom: 0;
  }

  .bbm-skeleton--image-lg {
    height: 256px;
  }

  .bbm-skeleton--title {
    height: 20px;
    width: 70%;
    margin-bottom: 12px;
  }

  .bbm-skeleton--text {
    height: 14px;
    width: 90%;
    margin-bottom: 8px;
  }

  .bbm-skeleton--btn {
    height: 36px;
    width: 100%;
    margin-top: 16px;
    border-radius: 8px;
  }

  // Empty state
  .bbm-empty {
    grid-column: 1 / -1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 64px 0;
    color: #94a3b8;
    gap: 12px;
  }

  .bbm-empty__icon {
    font-size: 3rem;
    opacity: 0.5;
  }

  // Help CTA
  .bbm-help-cta {
    background: rgba(0, 204, 249, 0.08);
    border: 1px solid rgba(0, 204, 249, 0.2);
    border-radius: 12px;
    padding: 32px;
    display: flex;
    flex-direction: column;
    gap: 24px;
    align-items: flex-start;

    @media (min-width: 768px) {
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
    }
  }

  .bbm-help-cta__icon-wrap {
    width: 64px;
    height: 64px;
    background: rgba(0, 204, 249, 0.15);
    border-radius: 9999px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .bbm-help-cta__icon {
    color: $bbm-secondary;
    font-size: 2rem;
  }

  .bbm-help-cta__title {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 1.25rem;
    font-weight: 600;
    color: #191c1e;
    margin: 0 0 4px;
  }

  .bbm-help-cta__body {
    font-size: 0.9375rem;
    color: #475569;
    margin: 0;
  }

  // Buttons
  .bbm-btn-primary {
    padding: 12px 32px;
    background: $bbm-primary;
    color: white;
    font-family: 'Space Grotesk', sans-serif;
    font-weight: 700;
    font-size: 0.9375rem;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    white-space: nowrap;
    transition: opacity 0.15s;

    &:hover {
      opacity: 0.88;
    }
  }

  .bbm-btn-accent {
    width: 100%;
    padding: 12px 0;
    background: $bbm-accent;
    color: white;
    font-weight: 700;
    font-size: 0.9375rem;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.2s, transform 0.1s;

    &:hover {
      background: $bbm-accent-dark;
    }

    &:active {
      transform: scale(0.98);
    }
  }

  // Map section
  .bbm-map-section {
    background: #f8fafc;
    border-top: 1px solid #e2e8f0;
    padding: 48px 0 0;
  }

  .bbm-map-section__sub {
    font-size: 1rem;
    color: #64748b;
    margin: 8px 0 0;
  }

  .bbm-map-frame-wrap {
    width: 100%;
    height: 600px;
    background: #e2e8f0;
    margin-top: 32px;
  }

  .bbm-map-frame {
    width: 100%;
    height: 100%;
    border: none;
    display: block;
  }


  @media (max-width: 768px) {
    .bbm-dir__main {
      padding: 0 16px 64px;
    }

    .bbm-hero {
      padding: 32px 24px;
    }

    .bbm-subnav__inner {
      padding: 0 16px;
    }
  }
</style>
